<template>
  <div class="w-100 px-0">
    <div class="row justify-content-start" v-if="initialized">
      <!-- category -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Category</h6>
        <CategoryFilter @update="categoryUpdate" :category="filteredCategory" />
      </div>

      <!-- sentiment -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Sentiment</h6>
        <SentimentFilter @update="sentimentUpdate" :sentiment="filteredSentiment" />
      </div>

      <!-- date -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Date Range</h6>
        <DatePicker
          @update="dateUpdate"
          :minDate="selectOldestCompanyStartDate([activeCompany._id])"
          :dateRange="filteredDateRange"
        />
      </div>

      <!-- location -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Locations</h6>
        <GroupingFilter
          :nonGroupMembers="locations"
          nonGroupMemberLabel="Locations"
          memberType="location"
          @update="groupingUpdate"
          :init="{
            nonGroupMemberIds: this.filteredLocations,
            groupIds: this.filteredGroups
          }"
        />
      </div>

      <!-- brands -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Brands</h6>
        <GenericSingleSelect
          :options="selectBrandOptions"
          @update="brandUpdate"
          :init="brandInitValue"
          :placeholder="'Select brand'"
          labelKey="label"
          trackByKey="value"
          emitKey="value"
        />
      </div>

      <!-- keywords -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Keyword Search</h6>
        <TagFilter
          placeholder="Use a comma to search multiple keywords"
          @apply="keywordUpdate"
          :init="filteredKeywords"
        />
      </div>

      <!-- Add Filter Button -->
      <div class="col-auto dropdown">
        <h6 class="header-pretitle mt-3">&nbsp;</h6>
        <button class="btn btn-primary" @click="formatAndApplyFilters">Filter</button>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from '@/components/Modules/Filters/DatePicker'
import CategoryFilter from '@/components/Modules/InsightsV2/Filters/CategoryFilter'
import SentimentFilter from '@/components/Modules/InsightsV2/Filters/SentimentFilter'
import GroupingFilter from '@/components/Modules/Filters/GroupingFilterV2'
import TagFilter from '@/components/Modules/Filters/TagFilterV2'
import GenericSingleSelect from '@/components/Modules/Filters/GenericSingleSelect'
import { createNamespacedHelpers } from 'vuex'

const CompetitionDetailsModule = createNamespacedHelpers('competitionDetails')
const CompanyModule = createNamespacedHelpers('company')
const LocationModule = createNamespacedHelpers('location')

export default {
  name: 'CompetitionDetailsFilters',
  components: {
    GroupingFilter,
    DatePicker,
    CategoryFilter,
    SentimentFilter,
    TagFilter,
    GenericSingleSelect
  },
  data: () => ({
    initialized: false,
    initialLoadComplete: false,
    filteredDateRange: null,
    filteredGroups: null,
    filteredGroupMembers: null,
    filteredLocations: null,
    filteredCategory: null,
    filteredSentiment: null,
    filteredKeywords: null,
    filteredBrand: null
  }),
  mounted() {
    this.initializeData()
  },
  computed: {
    ...LocationModule.mapGetters([
      'selectActiveLocations',
      'selectDisabledLocations',
      'selectLocation',
      'selectInsightsLocations'
    ]),
    ...CompanyModule.mapGetters(['activeCompany', 'selectOldestCompanyStartDate']),
    ...CompetitionDetailsModule.mapGetters(['selectBrandOptions']),
    locations() {
      return this.selectActiveLocations.filter((l) => !l.hideFromReports)
    },
    brandInitValue() {
      if (!this.filteredBrand || !this.selectBrandOptions.length) {
        return null
      }
      return this.selectBrandOptions.find((option) => option.value === this.filteredBrand) || null
    }
  },
  methods: {
    ...CompetitionDetailsModule.mapActions(['applyFilters']),
    async initializeData() {
      this.filteredDateRange = this.$route.query.dateRange
      this.filteredSentiment = this.$route.query.sentiment
      this.filteredCategory = this.$route.query.category
      this.filteredLocations = this.$route.query.locs
      this.filteredGroups = this.$route.query.groups
      this.filteredDisabledLocations = this.$route.query.disLocs
      this.filteredKeywords = this.$route.query.keywords
      this.filteredBrand = this.$route.query.brand
      if (this.filteredGroups && !Array.isArray(this.filteredGroups)) {
        this.filteredGroups = [this.filteredGroups]
      }
      if (this.filteredLocations && !Array.isArray(this.filteredLocations)) {
        this.filteredLocations = [this.filteredLocations]
      }
      if (this.filteredDisabledLocations && !Array.isArray(this.filteredDisabledLocations)) {
        this.filteredDisabledLocations = [this.filteredDisabledLocations]
      }
      if (this.filteredKeywords && !Array.isArray(this.filteredKeywords)) {
        this.filteredKeywords = [this.filteredKeywords]
      }

      if (!(await this.accessPermitted())) {
        this.$router.push('/competition/summary')
        this.$notify({ text: 'You lack permissions needed to view this link', type: 'error' })
      } else {
        this.initialized = true
      }
    },

    categoryUpdate(val) {
      this.filteredCategory = val
    },
    sentimentUpdate(val) {
      this.filteredSentiment = val
    },
    groupingUpdate(val) {
      this.filteredGroups = val && val.groupIds && val.groupIds.length ? val.groupIds : null
      this.filteredGroupMembers =
        val && val.memberIds && val.memberIds.length ? val.memberIds : null
      this.filteredLocations =
        val && val.locationIds && val.locationIds.length ? val.locationIds : null
    },
    dateUpdate(val) {
      this.filteredDateRange = val
      if (!this.initialLoadComplete) {
        this.formatAndApplyFilters()
        this.initialLoadComplete = true
      }
    },
    keywordUpdate(val) {
      this.filteredKeywords = [...val]
    },
    brandUpdate(val) {
      this.filteredBrand = val
    },
    async formatAndApplyFilters() {
      let locationsToFilter = [...(this.filteredLocations || [])]
      await this.applyFilters({
        groups: this.filteredGroups?.length ? this.filteredGroups : null,
        locations: locationsToFilter.length ? locationsToFilter : null,
        dateRange:
          this.filteredDateRange && Object.keys(this.filteredDateRange).length
            ? this.filteredDateRange
            : null,
        category: this.filteredCategory,
        sentiment: this.filteredSentiment,
        keywords: this.filteredKeywords,
        brand: this.filteredBrand
      })
      this.$emit('filtersApplied')
      this.$router
        .replace({
          path: this.$route.path,
          query: {
            dateRange: this.filteredDateRange,
            category: this.filteredCategory,
            sentiment: this.filteredSentiment,
            ...(this.filteredGroups && this.filteredGroups.length
              ? { groups: this.filteredGroups }
              : {
                  ...(this.filteredLocations?.length ? { locs: this.filteredLocations } : {})
                }),
            ...(this.filteredKeywords?.length ? { keywords: this.filteredKeywords } : {}),
            ...(this.filteredBrand ? { brand: this.filteredBrand } : {})
          }
        })
        .catch((err) => {
          if (err.name !== 'NavigationDuplicated') throw err
        })
    },
    async accessPermitted() {
      let permitted = true
      if (this.filteredLocations?.length) {
        permitted = this.filteredLocations.every((l) =>
          this.selectInsightsLocations.find((il) => il._id === l)
        )
      }
      return permitted
    }
  }
}
</script>
